import {
  TRANSLATABLE_DOCUMENTS,
  englishPath,
  type TranslatableDocument,
} from '../i18n/documents';
import { sourceHash } from '../i18n/sourceHash';
import type { ServerDocument } from '../services/i18nService';
import type { RowCounts, RowState } from './translationRows';

export interface DocumentRow {
  doc: TranslatableDocument;
  state: RowState;
  /** Null when nothing is saved (the language uses the built-in draft, or English). */
  saved: ServerDocument | null;
  hasBuiltInDraft: boolean;
  englishHash: string;
}

const englishCache = new Map<string, Promise<string>>();

/** English source text of a document (cached for the session). */
export function loadEnglishDocument(doc: TranslatableDocument): Promise<string> {
  let pending = englishCache.get(doc.id);
  if (!pending) {
    pending = fetch(englishPath(doc)).then((res) => {
      if (!res.ok) throw new Error(`Couldn't load ${doc.file}`);
      return res.text();
    });
    pending.catch(() => englishCache.delete(doc.id));
    englishCache.set(doc.id, pending);
  }
  return pending;
}

/** Fingerprints of every document's English, keyed by document id. Missing files are skipped. */
export async function loadEnglishHashes(): Promise<Record<string, string>> {
  const entries = await Promise.all(
    TRANSLATABLE_DOCUMENTS.map(async (doc) => {
      try {
        return [doc.id, sourceHash(await loadEnglishDocument(doc))] as const;
      } catch {
        return [doc.id, ''] as const;
      }
    })
  );
  return Object.fromEntries(entries);
}

export function buildDocumentRows(
  saved: ServerDocument[],
  code: string,
  englishHashes: Record<string, string>
): DocumentRow[] {
  const byId = new Map(saved.map((d) => [d.id, d]));
  return TRANSLATABLE_DOCUMENTS.map((doc) => {
    const row = byId.get(doc.id) ?? null;
    const englishHash = englishHashes[doc.id] || '';
    const hasBuiltInDraft = doc.builtInDrafts.includes(code);
    let state: RowState;
    if (row) {
      const stale = Boolean(englishHash && row.source_hash && row.source_hash !== englishHash);
      state = stale ? 'stale' : row.status;
    } else {
      state = hasBuiltInDraft ? 'ai_draft' : 'missing';
    }
    return { doc, state, saved: row, hasBuiltInDraft, englishHash };
  });
}

export function countDocumentRows(rows: DocumentRow[]): RowCounts {
  const counts: RowCounts = { total: rows.length, missing: 0, stale: 0, ai_draft: 0, draft: 0, approved: 0 };
  for (const r of rows) counts[r.state] += 1;
  return counts;
}

const SECTION_TAG_RE = /<<[^<>:]+? (?:start|end)>>/g;

/**
 * Structural tags the app looks for (like <<step-three start>>) that are in the
 * English but missing from the translation. Title and subtitle tags only need
 * to appear the same number of times; their text is translated.
 */
export function missingDocumentTags(english: string, translation: string): string[] {
  const missing = Array.from(new Set(english.match(SECTION_TAG_RE) ?? [])).filter(
    (tag) => !translation.includes(tag)
  );
  for (const name of ['title', 'subtitle']) {
    const count = (text: string) => text.split(`<<${name}:`).length - 1;
    const need = count(english);
    if (need > count(translation)) missing.push(`<<${name}: …>> (${need} in English)`);
  }
  return missing;
}
